import usePolaris from 'src/hooks/usePolaris';
import { NEW_INTERVIEWS, STATS } from '../constants';
import { hasInterviewCompleted, hasInterviewStarted } from '../utils';
import CircularProgress from '@mui/material/CircularProgress';
import StarOutline from '@mui/icons-material/StarOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import type { IInterviewConfig, MiniInterviewType } from 'src/packages/polaris/types';

const StatsAndStatus = ({
  interviewConfigs = [],
  interviewType,
}: {
  /** Pass through interview configs if you need to display status (in progress, completed, etc.). If this is undefined, only stats will display */
  interviewConfigs?: IInterviewConfig[];
  interviewType: MiniInterviewType;
}) => {
  const { state } = usePolaris();
  const subActors = state?.context?.subActors;

  const stats = STATS[interviewType];
  const { time = 0, count = 0 } = stats || {};

  const interviewContext = subActors[interviewConfigs[0]?.id]?.state?.context;
  let interviewCompleted = hasInterviewCompleted(interviewContext);
  let interviewStarted = hasInterviewStarted(interviewContext);

  if (interviewConfigs.length > 1) {
    // In this case we have to check all interview segments
    const interviewSegmentContexts = interviewConfigs.map(
      (config) => subActors[config.id]?.state?.context,
    );
    interviewStarted = !!interviewSegmentContexts?.find(hasInterviewStarted);
    interviewCompleted = !!interviewSegmentContexts?.find(hasInterviewCompleted);
  }

  const interviewInProgress = interviewStarted && !interviewCompleted;
  const shouldShowStats = !!stats && !interviewStarted && !interviewCompleted;

  return (
    <span className="bp-mt-1 bp-flex bp-items-center bp-text-xs bp-text-teal-50">
      {interviewCompleted && (
        <span className="bp-flex bp-items-center bp-text-primary-main bp-underline">
          Interview completed. Click to view results.
        </span>
      )}
      {interviewInProgress && (
        <span className="bp-flex bp-items-center bp-text-primary-main">
          <CircularProgress size={10} color="inherit" thickness={6} />
          <span className="bp-ml-1 bp-text-primary-main bp-underline">
            Interview in progress. Click to continue.
          </span>
        </span>
      )}
      {shouldShowStats && (
        <>
          <span className="bp-mr-2 bp-flex bp-items-center">
            <AccessTimeIcon fontSize="inherit" className="bp-mr-1" />
            {time < 60 ? '<1 min' : `${Math.ceil(time / 60)} min`}
          </span>
          {!NEW_INTERVIEWS.includes(interviewType) && (
            <span className="bp-flex bp-items-center">
              <StarOutline fontSize="inherit" className="bp-mr-1" />
              {count > 1000 ? `${Math.ceil(count / 1000)}k` : Math.ceil(count)} completed
            </span>
          )}
        </>
      )}
    </span>
  );
};

export default StatsAndStatus;
